import classNames from "classnames";
import { memo, useMemo } from "react";
import { FiltersTypes } from "@common/types/filters/general";
import { ReplaceProperties } from "../../../../common/types/base/generics";
import { Class } from "../../../../common/types/class";
import { GridTableRow } from "../../../general/Tables/GridTable/GridTable";
import { ExtendedFlattenedItem } from "../utils";
import styles from "../ClassesSettings.module.scss";
import { ClassIdCell } from "./ClassIdCell";

import { ClassActions } from "./ClassActions";

interface ClassItemProps {
    item: ExtendedFlattenedItem;
    collapsedClasses: Set<string>;
    setCollapsedClasses: (value: React.SetStateAction<Set<string>>) => void;
    projectedDepth?: number;
    isDragged?: boolean;
    percentage: number;
    transactionsCount: number;
    groupPercentage: number;
    groupTransactionsCount: number;
    filters: Pick<FiltersTypes, "end" | "start" | "entityIds" | "classes">;
}

export const ClassItem: React.FC<ClassItemProps> = memo(
    ({
        item,
        collapsedClasses,
        setCollapsedClasses,
        isDragged,
        projectedDepth,
        percentage,
        transactionsCount,
        groupPercentage,
        groupTransactionsCount,
        filters,
    }) => {
        const nameCell = useMemo(
            () => (
                <ClassIdCell
                    isCollapsed={collapsedClasses.has(item.id as string)}
                    item={
                        isDragged
                            ? {
                                  isEmpty: true,
                                  id: String(item.id),
                                  hasParent: item.depth > 0,
                              }
                            : (item as ReplaceProperties<
                                  ExtendedFlattenedItem,
                                  { meta: { classInstance: Class } }
                              >)
                    }
                />
            ),
            [item, isDragged, collapsedClasses],
        );

        const isCollapsed = useMemo(
            () => collapsedClasses.has(item.id as string),
            [collapsedClasses, item],
        );

        const percentageToDisplay = useMemo(
            () => (isCollapsed ? groupPercentage : percentage),
            [isCollapsed, groupPercentage, percentage],
        );
        const transactionsCountToDisplay = useMemo(
            () => (isCollapsed ? groupTransactionsCount : transactionsCount),
            [isCollapsed, groupTransactionsCount, transactionsCount],
        );

        const onClickCallback = useMemo(() => {
            let onClick = undefined;
            if (
                "classInstance" in item.meta &&
                !item.meta.classInstance.parentClassId
            ) {
                onClick = () => {
                    setCollapsedClasses((prev) => {
                        const newSet = new Set(prev);
                        if (newSet.has(item.id as string)) {
                            newSet.delete(item.id as string);
                        } else {
                            newSet.add(item.id as string);
                        }
                        return newSet;
                    });
                };
            }
            return onClick;
        }, [item, setCollapsedClasses]);

        const transactionsLink = useMemo(
            () =>
                `/transactions?filters=${JSON.stringify({ ...filters, classes: [item.id, ...(isCollapsed ? item.children.map((child) => child.id) : [])] })}`,
            [item.id, filters, isCollapsed, item.children],
        );

        const cells = useMemo(
            () => [
                {
                    key: "name",
                    value: nameCell,
                },
                {
                    key: "transactionPercentage",
                    value: isNaN(percentageToDisplay) ? null : (
                        <div className={styles.transactionCount}>
                            {percentageToDisplay.toFixed(1)}%
                        </div>
                    ),
                },
                {
                    key: "transactionCount",
                    value: (
                        <div className={styles.transactionCount}>
                            {!isNaN(transactionsCountToDisplay) &&
                            transactionsCountToDisplay > 0 ? (
                                <a
                                    href={transactionsLink}
                                    target="_blank"
                                    className={classNames(styles.label)}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    {transactionsCountToDisplay}
                                </a>
                            ) : (
                                <span
                                    className={classNames(
                                        styles.label,
                                        styles.zero,
                                    )}
                                >
                                    0
                                </span>
                            )}
                        </div>
                    ),
                },
                {
                    key: "actions",
                    value: (
                        <ClassActions classInstance={item.meta.classInstance} />
                    ),
                },
            ],
            [
                item.meta.classInstance,
                nameCell,
                percentageToDisplay,
                transactionsCountToDisplay,
                transactionsLink,
            ],
        );

        return (
            <GridTableRow
                onClick={onClickCallback}
                key={item.id}
                cells={cells}
                className={classNames(styles.row, {
                    [styles.subclassRow]:
                        (!isDragged &&
                            !!item.meta.classInstance.parentClassId) ||
                        (isDragged && projectedDepth && projectedDepth > 0),
                    [styles.bottomBorder]: collapsedClasses.has(
                        item.id as string,
                    ),
                    [styles.isCollapsed]:
                        collapsedClasses.has(item.parentId as string) ||
                        item.meta.isHidden,
                })}
                columnBorders={false}
            />
        );
    },
);
